import type {
  ClientRepresentationView,
  RepresentativeInvitationConfig,
  RepresentativeInvitationSummary,
} from "@closer/shared";
import { CLIENT_REPRESENTATION_STATUS_NAMES } from "@closer/shared";
import { Badge, Button, Card, FormField, Select, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * MÜVEKKİL TEMSİL İLİŞKİSİ (CLOS-178 `[132]`).
 *
 * ### ⚠ Temsilin tek meşru kaynağı KABUL EDİLMİŞ DAVET
 *
 * Ekran serbest bir "temsilci ekle" formu sunmuyor; kişi yalnız kabul edilmiş bir davetten
 * seçilebiliyor. Serbest form olsaydı büro, karşı tarafın haberi olmadan kendine temsilci
 * atayabilirdi — kartın engellediği tam olarak bu.
 *
 * ### ⚠ Sona eren temsil GERİ GELMİYOR
 *
 * `ended` satırda hiçbir komut yok. Sunucu da aynı şeyi söylüyor ("sona eren temsil yeni
 * davet ister"); ekran orada bir düğme gösterseydi kullanıcı yetkinin geri gelebileceğini
 * sanırdı.
 *
 * ### ⚠ Kapsam SÖZLÜKTEN
 *
 * Kapsam anahtarları `/api/clients/representative-policy` üzerinden geliyor. Serbest metin
 * olsaydı iki temsilcinin "aynı" kapsamı iki farklı satırda iki farklı yazımla durur ve
 * hiçbir kural onların aynı olduğunu bilemezdi.
 */
export function ClientRepresentations({ clientId }: { clientId: string }) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();
  const queryClient = useQueryClient();
  const [davetSecimi, setDavetSecimi] = useState("");
  const [kapsamSecimi, setKapsamSecimi] = useState("");

  const yetkili = can("client.representative");

  const anahtar = tenantKey(tenantId, "client-representations", clientId);
  const temsiller = useQuery({
    enabled: yetkili,
    queryKey: anahtar,
    queryFn: async () =>
      (
        await api<ClientRepresentationView[]>(`/api/clients/${clientId}/representatives`, {
          tenantId,
        })
      ).data,
  });

  const politika = useQuery({
    enabled: yetkili,
    queryKey: tenantKey(tenantId, "representative-policy"),
    queryFn: async () =>
      (
        await api<RepresentativeInvitationConfig>("/api/clients/representative-policy", {
          tenantId,
        })
      ).data,
  });

  const davetler = useQuery({
    enabled: yetkili,
    queryKey: tenantKey(tenantId, "client-rep-invitations", clientId),
    queryFn: async () =>
      (
        await api<RepresentativeInvitationSummary[]>(
          `/api/clients/${clientId}/representative-invitations`,
          { tenantId },
        )
      ).data,
  });

  const yenile = () => queryClient.invalidateQueries({ queryKey: anahtar });

  /**
   * ⚠ Temsil kurulunca DAVET LİSTESİ de tazeleniyor: kullanılmış davet artık yeni bir temsilin
   * kaynağı olamaz (sunucu "sonlanmamış temsil ilişkisi zaten var" diyor). Yalnız temsilleri
   * tazeleseydik aynı davet seçilebilir görünmeye devam eder, kullanıcı reddedilecek bir işlemi
   * tekrar tekrar denerdi.
   */
  const kaynagiTazele = () =>
    queryClient.invalidateQueries({
      queryKey: tenantKey(tenantId, "client-rep-invitations", clientId),
    });

  const kur = useMutation({
    mutationFn: (girdi: { invitationId: string; scope: string }) =>
      api<ClientRepresentationView>(`/api/clients/${clientId}/representatives`, {
        method: "POST",
        tenantId,
        body: JSON.stringify(girdi),
      }),
    onSuccess: async () => {
      setDavetSecimi("");
      setKapsamSecimi("");
      await yenile();
      await kaynagiTazele();
      toast.success("Temsil ilişkisi kuruldu");
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Temsil ilişkisi kurulamadı"),
  });

  const kapsamDegistir = useMutation({
    mutationFn: (girdi: { repId: string; scope: string }) =>
      api(`/api/clients/${clientId}/representatives/${girdi.repId}/scope`, {
        method: "PUT",
        tenantId,
        body: JSON.stringify({ scope: girdi.scope }),
      }),
    onSuccess: async () => {
      await yenile();
      toast.success("Temsil kapsamı güncellendi");
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Kapsam güncellenemedi"),
  });

  const gecis = useMutation({
    mutationFn: (girdi: { repId: string; komut: "suspend" | "end" | "reactivate" }) =>
      api(`/api/clients/${clientId}/representatives/${girdi.repId}/${girdi.komut}`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({}),
      }),
    onSuccess: async () => {
      await yenile();
      toast.success("Temsil durumu güncellendi");
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Durum değiştirilemedi"),
  });

  if (!yetkili) return null;

  const kapsamlar = politika.data?.scopeTypes ?? [];
  /** ⚠ Yalnız KABUL EDİLMİŞ davet: temsilin başka meşru kaynağı yok. */
  const kabulEdilenler = (davetler.data ?? []).filter((d) => d.status === "accepted");
  const kapsamAdi = (anahtarDegeri: string) =>
    kapsamlar.find((k) => k.key === anahtarDegeri)?.label ?? anahtarDegeri;

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <h2 className="text-base font-semibold text-fg-heading">Temsil ilişkileri</h2>

        {kabulEdilenler.length === 0 ? (
          <p className="text-xs text-fg-soft">
            Temsil ilişkisi yalnız kabul edilmiş bir davetten kurulur; kabul edilmiş davet yok.
          </p>
        ) : (
          <div className="flex flex-col gap-2 rounded-md border border-border-default p-3">
            <FormField label="Kabul edilmiş davet">
              <Select
                id="temsil-davet"
                value={davetSecimi}
                onChange={(olay) => setDavetSecimi(olay.target.value)}
              >
                <option value="">Seçiniz</option>
                {kabulEdilenler.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.email}
                  </option>
                ))}
              </Select>
            </FormField>

            <FormField label="Temsil kapsamı">
              <Select
                id="temsil-kapsam"
                value={kapsamSecimi}
                onChange={(olay) => setKapsamSecimi(olay.target.value)}
              >
                <option value="">Seçiniz</option>
                {kapsamlar.map((k) => (
                  <option key={k.key} value={k.key}>
                    {k.label}
                  </option>
                ))}
              </Select>
            </FormField>

            <div className="flex justify-end">
              <Button
                type="button"
                size="sm"
                disabled={!davetSecimi || !kapsamSecimi}
                loading={kur.isPending}
                onClick={() => kur.mutate({ invitationId: davetSecimi, scope: kapsamSecimi })}
              >
                Temsil ilişkisini kur
              </Button>
            </div>
          </div>
        )}

        {temsiller.isPending ? (
          <div role="status" aria-label="Temsil ilişkileri yükleniyor">
            <Skeleton lines={2} />
          </div>
        ) : temsiller.error ? (
          <StateView
            state={resolveErrorState(temsiller.error, { idempotent: true })}
            onRetry={() => void temsiller.refetch()}
          />
        ) : (temsiller.data ?? []).length === 0 ? (
          <p className="text-sm text-fg-soft">Bu müvekkil için kurulmuş temsil ilişkisi yok.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {(temsiller.data ?? []).map((temsil) => (
              <li
                key={temsil.id}
                className="flex flex-col gap-2 rounded-md border border-border-default p-3"
              >
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium text-fg-strong">{temsil.representativeName}</span>
                  <Badge tone={temsil.status === "active" ? "success" : "neutral"}>
                    {CLIENT_REPRESENTATION_STATUS_NAMES[temsil.status]}
                  </Badge>
                  <span className="text-xs text-fg-soft">{kapsamAdi(temsil.scope)}</span>
                  <span className="text-xs text-fg-soft">
                    {new Date(temsil.endsAt).toLocaleDateString("tr-TR")} tarihine kadar
                  </span>
                </div>

                {/* ⚠ Sona ermiş temsilde HİÇBİR komut yok: yetki yeni davetle geri gelir. */}
                {temsil.status !== "ended" && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Select
                      aria-label={`${temsil.representativeName} kapsamı`}
                      className="w-auto"
                      value={temsil.scope}
                      onChange={(olay) =>
                        kapsamDegistir.mutate({ repId: temsil.id, scope: olay.target.value })
                      }
                    >
                      {kapsamlar.map((k) => (
                        <option key={k.key} value={k.key}>
                          {k.label}
                        </option>
                      ))}
                    </Select>

                    {temsil.status === "active" && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => gecis.mutate({ repId: temsil.id, komut: "suspend" })}
                      >
                        Askıya al
                      </Button>
                    )}
                    {temsil.status === "suspended" && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => gecis.mutate({ repId: temsil.id, komut: "reactivate" })}
                      >
                        Yeniden aktifleştir
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => gecis.mutate({ repId: temsil.id, komut: "end" })}
                    >
                      Temsili sonlandır
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
